import { BigFormRow } from 'framework/ui/layout';
import { useRecruitmentApi } from 'infrastructure/api';
import { ModalForm, FormFieldsData } from 'framework/form';
import {
  CandidateApplicationSource,
  CandidateApplicationSourceType,
  CandidateApplicationSourceTypeOptions,
  getSourceOptions,
} from '../shared/candidateApplicationService';
import { useShowContext } from 'framework/show';
import createApplicationValidation from 'domain/recruiting/candidate/applications/resubmit/createApplicationValidation';
import { CandidateApplication } from 'domain/shared/candidate';
import { Box } from '@mui/material';
import { TextField } from 'framework/ui/field';
import { NoteAttachmentUploaderInput } from 'domain/shared/artifact/input';
import { TimelineFeedbackWysiwygInput } from 'domain/shared/employee';

interface ApplicationFormProps {
  applicationId: number;
  closeModal: () => void;
  modalOpen: boolean;
}

type CandidateApplicationRequestType = {
  candidateId: string;
  note: {
    attachmentsIds: string[];
    noteText: string;
  };
  source: string;
  sourceType: CandidateApplicationSourceType;
  sourceComment?: string | null;
  vacancyAlias: string;
};

const ResubmitCandidateApplicationModalForm = ({ applicationId, closeModal, modalOpen }: ApplicationFormProps) => {
  const { sendPostRequest } = useRecruitmentApi();
  const { data: contextData } = useShowContext();

  const getValidationSchema = () => Promise.resolve(createApplicationValidation);

  const applicationToResubmit: CandidateApplication = contextData.applications.result.find(
    (app: CandidateApplication) => app.id === applicationId,
  );

  const { candidateId } = applicationToResubmit;
  const candidateName = contextData.name.fullName;
  const { source } = applicationToResubmit;
  const sourceValue =
    source && source in CandidateApplicationSource ? getSourceOptions()[source as CandidateApplicationSource] : source;
  const sourceType: CandidateApplicationSourceType =
    CandidateApplicationSourceType[applicationToResubmit.sourceType as keyof typeof CandidateApplicationSourceType];
  const sourceTypeValue = CandidateApplicationSourceTypeOptions[sourceType];
  const { sourceComment } = applicationToResubmit;
  const vacancyAlias = applicationToResubmit.vacancyGroupAlias;
  const { specialization } = contextData.vacancyGroups[vacancyAlias];

  const createApplication = async (data: FormFieldsData) => {
    const result = await sendPostRequest('/candidates/applications/active-sourcing', {
      ...(data as CandidateApplicationRequestType),
      sourceApplicationId: applicationId,
    });

    return result.json();
  };

  return (
    <>
      <Box sx={{ mb: 1 }}>
        <TextField label="Full name" value={candidateName} />
      </Box>
      <Box sx={{ mb: 1 }}>
        <TextField label="Applied to" value={specialization} />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, width: 1 / 2 }}>
        <TextField label="Source Type" value={sourceTypeValue} />
        <TextField label="Source" value={sourceValue} />
      </Box>
      {sourceComment && (
        <Box sx={{ mb: 1 }}>
          <TextField label="Source comment" value={sourceComment} />
        </Box>
      )}
      <ModalForm
        closeModal={closeModal}
        modalOpen={modalOpen}
        initialData={{
          candidateId,
          source,
          sourceType,
          sourceComment,
          vacancyAlias,
        }}
        submitFormFunc={createApplication}
        afterSubmit={{
          execute: () => {
            window.location.reload();
            closeModal();
          },
        }}
        getValidationSchemaFunc={getValidationSchema}
      >
        <BigFormRow>
          <TimelineFeedbackWysiwygInput
            label="Note"
            labelHint="(Will be added to candidate timeline)"
            source="note.noteText"
            artifactsSource="note.attachmentsIds"
          />
          <NoteAttachmentUploaderInput label="Attachments" source="note.attachmentsIds" />
        </BigFormRow>
      </ModalForm>
    </>
  );
};

export default ResubmitCandidateApplicationModalForm;
